import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GraduationCap, Plus, Users, BookOpen, CheckCircle2, XCircle, Sparkles, ClipboardList, Trophy } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, StatCard, Spinner, EmptyState, GameCard } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function SchoolDashboard() {
  const navigate = useNavigate();
  const [dash, setDash] = useState(null);
  const [classes, setClasses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [newClass, setNewClass] = useState({ name: "", description: "" });
  const [classOpen, setClassOpen] = useState(false);
  const [student, setStudent] = useState({ name: "", username: "", password: "" });
  const [studentOpen, setStudentOpen] = useState(false);
  const [assign, setAssign] = useState({ title: "", description: "", template: null, due_days: 7 });
  const [assignOpen, setAssignOpen] = useState(false);

  const loadTop = () => Promise.all([api.get("/school/dashboard"), api.get("/school/classes"), api.get("/school/lessons")])
    .then(([d, c, l]) => { setDash(d.data); setClasses(c.data); setLessons(l.data); if (!selected && c.data[0]) setSelected(c.data[0].id); });
  const loadDetail = (id) => api.get(`/school/classes/${id}`).then((r) => setDetail(r.data));

  useEffect(() => { loadTop(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (selected) loadDetail(selected); }, [selected]);

  if (!dash) return <Spinner />;

  const createClass = async () => {
    try { const { data } = await api.post("/school/classes", newClass); toast.success(`Class created! Join code: ${data.code}`); setClassOpen(false); setNewClass({ name: "", description: "" }); setSelected(data.id); loadTop(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const addStudent = async () => {
    try { const { data } = await api.post(`/school/classes/${selected}/students`, student); toast.success(`Student added! Login: ${data.login_username}`); setStudentOpen(false); setStudent({ name: "", username: "", password: "" }); loadDetail(selected); loadTop(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const addAssignment = async () => {
    try { await api.post(`/school/classes/${selected}/assignments`, { ...assign, due_days: Number(assign.due_days) }); toast.success("Assignment created!"); setAssignOpen(false); setAssign({ title: "", description: "", template: null, due_days: 7 }); loadDetail(selected); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const decide = async (id, action) => { const { data } = await api.post(`/school/approvals/${id}/${action}`); toast.success(data.message); loadDetail(selected); loadTop(); };
  const assignLesson = (l) => {
    if (!selected) { toast.error("Create or select a class first to assign a lesson."); return; }
    setAssign({ title: l.title, description: l.desc, template: l.template, due_days: 7 });
    setAssignOpen(true);
  };

  return (
    <div className="p-5 lg:p-8 max-w-6xl mx-auto">
      <PageHeader title="GameView School" icon={GraduationCap} subtitle="Classes, students, assignments and a safe class showcase."
        action={
          <Dialog open={classOpen} onOpenChange={setClassOpen}>
            <DialogTrigger asChild><Button className="rounded-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]" data-testid="add-class-btn"><Plus className="w-4 h-4" /> New Class</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display">Create a class</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Class name</Label><Input value={newClass.name} onChange={(e) => setNewClass((f) => ({ ...f, name: e.target.value }))} placeholder="Year 5 Coding Club" data-testid="class-name" className="mt-1.5" /></div>
                <div><Label>Description</Label><Textarea value={newClass.description} onChange={(e) => setNewClass((f) => ({ ...f, description: e.target.value }))} className="mt-1.5" /></div>
              </div>
              <DialogFooter><Button onClick={createClass} data-testid="create-class-confirm" className="bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]">Create class</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        } />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <StatCard label="Classes" value={dash.classes} icon={BookOpen} delay={1} />
        <StatCard label="Students" value={dash.students} icon={Users} accent="#10b981" delay={2} />
        <StatCard label="Assignments" value={dash.assignments} icon={ClipboardList} accent="#a855f7" delay={3} />
        <StatCard label="Pending" value={dash.pending_approvals} icon={CheckCircle2} accent="#f59e0b" delay={4} />
        <StatCard label="Showcase" value={dash.showcase_games} icon={Trophy} accent="#ef4444" delay={5} />
      </div>

      {classes.length === 0 ? (
        <EmptyState emoji="🏫" title="No classes yet" text="Create your first class to add students and assignments." />
      ) : (
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Class list */}
          <div className="space-y-2">
            {classes.map((c) => (
              <button key={c.id} onClick={() => setSelected(c.id)} data-testid={`class-${c.id}`}
                className={`w-full text-left p-4 rounded-2xl border transition-all ${selected === c.id ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10" : "border-[hsl(var(--border))] bg-[hsl(var(--card))]"}`}>
                <div className="font-bold">{c.name}</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">Code {c.code} · {c.student_count} students</div>
              </button>
            ))}
          </div>

          {/* Detail */}
          <div className="lg:col-span-3 space-y-5">
            {!detail ? <Spinner /> : (
              <>
                {/* Share / join code */}
                <div className="rounded-2xl border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 p-4 flex flex-wrap items-center gap-3" data-testid="class-share-bar">
                  <div className="flex-1 min-w-[160px]">
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">Class join code</div>
                    <div className="font-mono font-extrabold text-2xl tracking-[0.25em]">{detail.class.code}</div>
                  </div>
                  <Button size="sm" variant="outline" className="rounded-full" data-testid="copy-code-btn"
                    onClick={() => { navigator.clipboard?.writeText(detail.class.code); toast.success("Code copied!"); }}>Copy code</Button>
                  <Button size="sm" variant="outline" className="rounded-full" data-testid="copy-link-btn"
                    onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/showcase/${detail.class.code}`); toast.success("Showcase link copied!"); }}>Copy showcase link</Button>
                  <Button size="sm" className="rounded-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]" data-testid="open-showcase-btn"
                    onClick={() => window.open(`/showcase/${detail.class.code}`, "_blank")}>Open showcase</Button>
                </div>

                {/* Class progress */}
                <ClassProgress students={detail.students} />

                {/* Approvals */}
                {detail.approvals.length > 0 && (
                  <div>
                    <h3 className="font-display font-bold mb-2">Projects to review</h3>
                    <div className="space-y-2">
                      {detail.approvals.map((r) => (
                        <div key={r.id} className="rounded-2xl border border-amber-500/30 bg-[hsl(var(--card))] p-3 flex items-center gap-3" data-testid={`school-approval-${r.id}`}>
                          <span className="text-2xl">{r.cover_emoji}</span>
                          <div className="flex-1 min-w-0"><div className="font-semibold truncate">{r.title}</div><div className="text-xs text-[hsl(var(--muted-foreground))]">by {r.child_name}</div></div>
                          <Button size="sm" className="rounded-full bg-emerald-500" onClick={() => decide(r.id, "approve")} data-testid={`school-approve-${r.id}`}><CheckCircle2 className="w-4 h-4" /></Button>
                          <Button size="sm" variant="outline" className="rounded-full" onClick={() => decide(r.id, "reject")}><XCircle className="w-4 h-4" /></Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Students */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-display font-bold">Students ({detail.students.length})</h3>
                    <Dialog open={studentOpen} onOpenChange={setStudentOpen}>
                      <DialogTrigger asChild><Button size="sm" variant="outline" className="rounded-full" data-testid="add-student-btn"><Plus className="w-4 h-4" /> Add Student</Button></DialogTrigger>
                      <DialogContent>
                        <DialogHeader><DialogTitle className="font-display">Add a student</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                          <div><Label>Name</Label><Input value={student.name} onChange={(e) => setStudent((f) => ({ ...f, name: e.target.value }))} data-testid="student-name" className="mt-1.5" /></div>
                          <div><Label>Username</Label><Input value={student.username} onChange={(e) => setStudent((f) => ({ ...f, username: e.target.value }))} data-testid="student-username" className="mt-1.5" /></div>
                          <div><Label>Password</Label><Input value={student.password} onChange={(e) => setStudent((f) => ({ ...f, password: e.target.value }))} data-testid="student-password" className="mt-1.5" /></div>
                        </div>
                        <DialogFooter><Button onClick={addStudent} data-testid="create-student-confirm" className="bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]">Add student</Button></DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                  {detail.students.length === 0 ? <p className="text-sm text-[hsl(var(--muted-foreground))]">No students yet.</p> : (
                    <div className="grid sm:grid-cols-2 gap-2">
                      {detail.students.map((s) => (
                        <div key={s.id} className="flex items-center gap-3 p-3 rounded-xl bg-[hsl(var(--card))] border border-[hsl(var(--border))]">
                          <span className="text-xl">{s.avatar_emoji}</span>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">{s.name}</div>
                            <div className="text-xs text-[hsl(var(--muted-foreground))]">@{s.username}</div>
                            <div className="flex gap-2 mt-1 text-[10px] font-bold">
                              <span className="px-1.5 py-0.5 rounded bg-[hsl(var(--secondary))]">🎮 {s.games_created}</span>
                              <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-600">⏳ {s.submitted}</span>
                              <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-600">✅ {s.published}</span>
                              <span className="px-1.5 py-0.5 rounded bg-[hsl(var(--secondary))]">🏆 {s.badges}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Assignments */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-display font-bold">Assignments</h3>
                    <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
                      <DialogTrigger asChild><Button size="sm" variant="outline" className="rounded-full" data-testid="add-assignment-btn"><Plus className="w-4 h-4" /> New</Button></DialogTrigger>
                      <DialogContent>
                        <DialogHeader><DialogTitle className="font-display">Create assignment</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                          <div><Label>Title</Label><Input value={assign.title} onChange={(e) => setAssign((f) => ({ ...f, title: e.target.value }))} data-testid="assignment-title" className="mt-1.5" /></div>
                          <div><Label>Instructions</Label><Textarea value={assign.description} onChange={(e) => setAssign((f) => ({ ...f, description: e.target.value }))} className="mt-1.5" /></div>
                          <div><Label>Due in (days)</Label><Input type="number" value={assign.due_days} onChange={(e) => setAssign((f) => ({ ...f, due_days: e.target.value }))} className="mt-1.5" /></div>
                        </div>
                        <DialogFooter><Button onClick={addAssignment} data-testid="create-assignment-confirm" className="bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]">Create</Button></DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                  {detail.assignments.length === 0 ? <p className="text-sm text-[hsl(var(--muted-foreground))]">No assignments yet.</p> : (
                    <div className="space-y-2">
                      {detail.assignments.map((a) => (
                        <div key={a.id} className="p-3 rounded-xl bg-[hsl(var(--card))] border border-[hsl(var(--border))]">
                          <div className="flex items-center justify-between gap-2">
                            <div className="font-semibold">{a.title}</div>
                            <div className="text-xs font-bold text-[hsl(var(--muted-foreground))] shrink-0">📥 {a.submissions} · ✅ {a.approved}</div>
                          </div>
                          <div className="text-xs text-[hsl(var(--muted-foreground))]">{a.description}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Showcase */}
                {detail.showcase.length > 0 && (
                  <div>
                    <h3 className="font-display font-bold mb-2">Class Showcase 🏆</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {detail.showcase.map((g) => <GameCard key={g.id} game={{ ...g, world: g.config?.world }} onClick={() => navigate(`/app/arcade/${g.id}`)} />)}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Lesson templates */}
      <h2 className="font-display text-xl font-bold mt-10 mb-3 flex items-center gap-2"><Sparkles className="w-5 h-5 text-[hsl(var(--primary))]" /> Lesson templates</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {lessons.map((l) => (
          <div key={l.title} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 card-hover">
            <div className="text-3xl mb-2">{l.icon}</div>
            <div className="font-bold">{l.title}</div>
            <div className="text-xs text-[hsl(var(--muted-foreground))] mb-3">{l.desc}</div>
            <div className="flex gap-2">
              <Button size="sm" className="rounded-full flex-1 bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]" onClick={() => assignLesson(l)} data-testid={`assign-lesson-${l.template}`}>Assign to class</Button>
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => navigate("/app/create")}>Try</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const ClassProgress = ({ students }) => {
  if (!students || students.length === 0) return null;
  const games = students.reduce((a, s) => a + (s.games_created || 0), 0);
  const subm = students.reduce((a, s) => a + (s.submitted || 0), 0);
  const pub = students.reduce((a, s) => a + (s.published || 0), 0);
  const badges = students.reduce((a, s) => a + (s.badges || 0), 0);
  return (
    <div className="grid grid-cols-4 gap-2" data-testid="class-progress">
      {[["Games", games, "🎮"], ["In review", subm, "⏳"], ["Published", pub, "✅"], ["Badges", badges, "🏆"]].map(([l, v, e]) => (
        <div key={l} className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3 text-center">
          <div className="text-xl">{e}</div>
          <div className="font-display text-xl font-extrabold">{v}</div>
          <div className="text-[10px] text-[hsl(var(--muted-foreground))]">{l}</div>
        </div>
      ))}
    </div>
  );
};
